import { useEffect, useRef, useState, useCallback } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import {
  Bot, Send, Loader2, Sparkles, Plus, ChevronDown, ScrollText, MessageSquare,
} from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { AssistantActionCard } from "@/components/admin/assistant/AssistantActionCard";
import { AssistantAuditPanel } from "@/components/admin/assistant/AssistantAuditPanel";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SUGGESTIONS = [
  "Dame un resumen general del negocio",
  "¿Qué pagos están vencidos?",
  "Muéstrame el catálogo de servicios con precios",
  "Sube 10% el precio de la Lectura Básica",
  "Aplica 20% de descuento a Magia Suprema por 7 días",
  "Cambia el horario de atención de 10:00 a 20:00",
];

export const AdminAssistant = () => {
  const { authHeader } = useAuth();
  const [open, setOpen] = useState(false);
  const [view, setView] = useState("chat"); // chat | audit
  const [sessionId, setSessionId] = useState(null);
  const [messages, setMessages] = useState([]); // {role, content, cards, actions}
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [busyAction, setBusyAction] = useState(null);
  const scrollRef = useRef(null);

  const scrollDown = () => setTimeout(() => { scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" }); }, 60);

  const loadSession = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/admin/assistant/session`, { headers: authHeader() });
      setSessionId(r.data.session_id);
      setMessages(r.data.messages || []);
      scrollDown();
    } catch { /* ignore */ }
  }, [authHeader]);

  useEffect(() => { if (open && messages.length === 0) loadSession(); }, [open, loadSession, messages.length]);

  const send = async (text) => {
    const body = (text ?? input).trim();
    if (!body || sending) return;
    setInput("");
    setMessages((prev) => [...prev, { role: "user", content: body }]);
    setSending(true);
    scrollDown();
    try {
      const r = await axios.post(`${API}/admin/assistant/message`, { session_id: sessionId, text: body }, { headers: authHeader() });
      setSessionId(r.data.session_id);
      setMessages((prev) => [...prev, { role: "assistant", content: r.data.reply, cards: r.data.cards || [], actions: r.data.pending_actions || [] }]);
      scrollDown();
    } catch (e) {
      setMessages((prev) => [...prev, { role: "assistant", content: e?.response?.data?.detail || "No pude responder, inténtalo de nuevo." }]);
    } finally { setSending(false); }
  };

  const newSession = async () => {
    try {
      const r = await axios.post(`${API}/admin/assistant/new-session`, {}, { headers: authHeader() });
      setSessionId(r.data.session_id);
      setMessages([]);
    } catch { /* ignore */ }
  };

  const setActionStatus = (actionId, status) =>
    setMessages((prev) => prev.map((m) => ({
      ...m, actions: (m.actions || []).map((a) => (a.id === actionId ? { ...a, status } : a)),
    })));

  const confirmAction = async (action) => {
    setBusyAction(action.id);
    try {
      const r = await axios.post(`${API}/admin/assistant/actions/${action.id}/confirm`, {}, { headers: authHeader() });
      setActionStatus(action.id, "done");
      toast.success(r.data?.result || "Acción ejecutada");
    } catch (e) {
      const status = e?.response?.status;
      if (status === 400) setActionStatus(action.id, "failed");
      toast.error(e?.response?.data?.detail || "No se pudo ejecutar");
    } finally { setBusyAction(null); }
  };

  const cancelAction = async (action) => {
    setBusyAction(action.id);
    try {
      await axios.post(`${API}/admin/assistant/actions/${action.id}/cancel`, {}, { headers: authHeader() });
      setActionStatus(action.id, "cancelled");
    } catch { /* ignore */ } finally { setBusyAction(null); }
  };

  return (
    <>
      {!open && (
        <button
          data-testid="admin-assistant-fab"
          onClick={() => setOpen(true)}
          className="fixed bottom-6 right-6 z-[120] h-14 w-14 rounded-full bg-gradient-to-br from-[#d4af37] to-amber-600 text-black shadow-[0_0_30px_rgba(212,175,55,0.5)] flex items-center justify-center hover:scale-105 transition"
          title="Asistente IA del panel"
        >
          <Bot className="w-6 h-6" />
        </button>
      )}

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: 20, scale: 0.98 }} animate={{ opacity: 1, y: 0, scale: 1 }} exit={{ opacity: 0, y: 20, scale: 0.98 }}
            data-testid="admin-assistant-panel"
            className="fixed bottom-4 right-4 z-[121] w-[min(420px,calc(100vw-2rem))] h-[min(620px,calc(100vh-2rem))] bg-[#0a0810] border border-[#d4af37]/40 rounded-2xl shadow-2xl flex flex-col overflow-hidden"
          >
            {/* Header */}
            <div className="px-4 py-3 border-b border-white/10 bg-gradient-to-r from-[#d4af37]/15 to-amber-500/10 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div className="h-8 w-8 rounded-full bg-gradient-to-br from-[#d4af37] to-amber-600 flex items-center justify-center"><Bot className="w-4 h-4 text-black" /></div>
                <div>
                  <div className="text-white font-serif text-sm leading-tight">Asistente IA</div>
                  <div className="text-[10px] text-[#d4af37]/80 flex items-center gap-1"><Sparkles className="w-2.5 h-2.5" /> CrisAngel Astral · Claude Sonnet 4.5</div>
                </div>
              </div>
              <div className="flex items-center gap-1">
                <button
                  data-testid="admin-assistant-toggle-audit"
                  onClick={() => setView(view === "audit" ? "chat" : "audit")}
                  title={view === "audit" ? "Volver al chat" : "Bitácora de cambios"}
                  className={`h-7 w-7 rounded-full flex items-center justify-center ${view === "audit" ? "bg-[#d4af37] text-black" : "bg-white/10 hover:bg-white/20 text-white"}`}
                >
                  {view === "audit" ? <MessageSquare className="w-4 h-4" /> : <ScrollText className="w-4 h-4" />}
                </button>
                <button data-testid="admin-assistant-new" onClick={newSession} title="Nueva conversación" className="h-7 w-7 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center"><Plus className="w-4 h-4" /></button>
                <button data-testid="admin-assistant-close" onClick={() => setOpen(false)} className="h-7 w-7 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center"><ChevronDown className="w-4 h-4" /></button>
              </div>
            </div>

            {view === "audit" && <AssistantAuditPanel />}

            {/* Messages */}
            {view === "chat" && (
            <div ref={scrollRef} className="flex-1 overflow-y-auto p-3 space-y-3">
              {messages.length === 0 && (
                <div className="text-center py-6">
                  <div className="h-12 w-12 rounded-full bg-[#d4af37]/15 flex items-center justify-center mx-auto mb-3"><Bot className="w-6 h-6 text-[#d4af37]" /></div>
                  <p className="text-sm text-neutral-300 mb-1">Hola, soy tu asistente del panel.</p>
                  <p className="text-xs text-neutral-500 mb-4">Consulto datos, gestiono el catálogo (servicios, precios, descuentos, categorías) y la configuración (horario, métodos de pago). Cada cambio requiere tu confirmación y queda en la bitácora.</p>
                  <div className="flex flex-col gap-2">
                    {SUGGESTIONS.map((s) => (
                      <button key={s} onClick={() => send(s)} className="text-left text-[12px] px-3 py-2 rounded-lg border border-white/10 bg-black/40 text-neutral-200 hover:border-[#d4af37]/40 hover:text-white">{s}</button>
                    ))}
                  </div>
                </div>
              )}

              {messages.map((m, idx) => (
                <div key={idx} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                  <div className={`max-w-[88%] ${m.role === "user" ? "" : "w-full"}`}>
                    <div
                      data-testid={`admin-assistant-msg-${m.role}`}
                      className={`rounded-2xl px-3.5 py-2.5 text-sm whitespace-pre-wrap ${m.role === "user" ? "bg-[#d4af37] text-black rounded-br-sm" : "bg-white/5 border border-white/10 text-neutral-100 rounded-bl-sm"}`}
                    >
                      {m.content}
                    </div>

                    {/* Option cards */}
                    {(m.cards || []).map((card, ci) => card.type === "image" ? (
                      <div key={ci} className="mt-2 rounded-xl border border-[#d4af37]/30 bg-black/40 p-2" data-testid="admin-assistant-image-card">
                        <div className="text-[10px] uppercase tracking-widest text-[#d4af37] mb-1.5">{card.title || "Imagen generada"}</div>
                        <img src={`${process.env.REACT_APP_BACKEND_URL}${card.url}`} alt={card.prompt || "Imagen IA"} className="w-full rounded-lg" loading="lazy" />
                        <div className="mt-1.5 flex gap-1.5 flex-wrap">
                          <button data-testid="admin-assistant-image-use-hero" onClick={() => send(`Coloca la imagen ${card.url} como imagen de fondo del hero (hero.image)`)} className="text-[11px] px-2.5 py-1 rounded-lg border border-[#d4af37]/40 text-[#d4af37] hover:bg-[#d4af37]/10">Usar en el hero</button>
                          <a href={`${process.env.REACT_APP_BACKEND_URL}${card.url}`} target="_blank" rel="noreferrer" className="text-[11px] px-2.5 py-1 rounded-lg border border-white/15 text-neutral-300 hover:bg-white/5">Abrir</a>
                        </div>
                      </div>
                    ) : (
                      <div key={ci} className="mt-2 rounded-xl border border-fuchsia-400/30 bg-fuchsia-500/5 p-2.5">
                        {card.title && <div className="text-[10px] uppercase tracking-widest text-fuchsia-300 mb-1.5">{card.title}</div>}
                        <div className="flex flex-wrap gap-1.5">
                          {(card.options || []).map((o, oi) => (
                            <button key={oi} data-testid="admin-assistant-card-option" onClick={() => send(o.prompt)} className="text-[12px] px-2.5 py-1.5 rounded-lg border border-fuchsia-400/40 bg-fuchsia-500/10 text-fuchsia-100 hover:bg-fuchsia-500/20">{o.label}</button>
                          ))}
                        </div>
                      </div>
                    ))}

                    {/* Pending-action confirmation cards */}
                    {(m.actions || []).map((a) => (
                      <AssistantActionCard key={a.id} action={a} busy={busyAction === a.id} onConfirm={confirmAction} onCancel={cancelAction} />
                    ))}
                  </div>
                </div>
              ))}

              {sending && (
                <div className="flex justify-start"><div className="rounded-2xl px-3.5 py-2.5 bg-white/5 border border-white/10"><Loader2 className="w-4 h-4 animate-spin text-[#d4af37]" /></div></div>
              )}
            </div>
            )}

            {/* Input */}
            {view === "chat" && (
            <div className="p-3 border-t border-white/10 bg-black/40">
              <div className="flex items-end gap-2">
                <textarea
                  data-testid="admin-assistant-input"
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
                  rows={1}
                  placeholder="Escribe tu petición…"
                  className="flex-1 bg-black/50 border border-white/10 rounded-xl px-3 py-2.5 text-sm text-white placeholder:text-neutral-600 focus:border-[#d4af37]/50 outline-none resize-none max-h-28"
                />
                <button data-testid="admin-assistant-send" onClick={() => send()} disabled={sending || !input.trim()} className="h-10 w-10 shrink-0 rounded-full bg-[#d4af37] hover:bg-[#f3d878] text-black flex items-center justify-center disabled:opacity-40">
                  {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                </button>
              </div>
            </div>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default AdminAssistant;
